import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Small colored dot to indicate a user's presence status.
 * Shows: live (red, pulsing) · online (emerald) · away (amber) · brb (purple) · offline (grey, hidden by default).
 * Accepts either a `status` prop or fetches `/users/{userId}/presence`.
 * When backend returns null/missing, defaults to "online" so active users always show some signal.
 */
const STATUS_META = {
  live: { color: "bg-red-500", label: "Live", ring: "ring-red-400/60", pulse: true },
  online: { color: "bg-emerald-500", label: "Online", ring: "ring-emerald-400/50" },
  away: { color: "bg-amber-500", label: "Away", ring: "ring-amber-400/60" },
  brb: { color: "bg-purple-500", label: "BRB", ring: "ring-purple-400/60" },
  offline: { color: "bg-gray-500", label: "Offline", ring: "ring-gray-500/40", hidden: true },
};

const PresenceDot = ({ status, userId, size = "md", showLabel = false, showOffline = false, className = "" }) => {
  const [resolved, setResolved] = useState(status || null);
  const [note, setNote] = useState(null);

  useEffect(() => {
    if (status !== undefined && status !== null) {
      setResolved(status);
      return;
    }
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/users/${userId}/presence`);
        if (!cancelled) {
          // If backend has no explicit status (or returns offline w/o note), treat as online so active users always show a signal.
          const raw = r.data?.presence_status;
          setResolved(raw && raw !== "" ? raw : "online");
          setNote(r.data?.presence_status_note || null);
        }
      } catch {
        if (!cancelled) setResolved("online");
      }
    })();
    return () => { cancelled = true; };
  }, [status, userId]);

  const meta = STATUS_META[resolved] || STATUS_META.online;
  if (meta.hidden && !showOffline) return null;

  const dotSize = size === "sm" ? "w-2 h-2" : size === "lg" ? "w-3.5 h-3.5" : "w-2.5 h-2.5";

  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      data-testid={`presence-dot-${resolved || 'online'}`}
      title={note ? `${meta.label} — ${note}` : meta.label}
    >
      <span
        className={`inline-block ${dotSize} rounded-full ${meta.color} ring-2 ${meta.ring} ${meta.pulse ? "animate-pulse" : ""}`}
      />
      {showLabel && (
        <span className="text-[10px] uppercase tracking-wider font-semibold text-white/80">
          {meta.label}
        </span>
      )}
    </span>
  );
};

export default PresenceDot;
export { STATUS_META };
